import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarPlus, EyeOff, ListChecks, Pencil, Plus, TriangleAlert } from 'lucide-react';
import {
  acknowledgePlanConflicts,
  createPrepTemplate,
  currentBasis,
  dismissPrepSuggestion,
  isPrepDismissed,
  planForSource,
  prepRuleIdFor,
  proposePlan,
  restorePrepSuggestion,
  starterPrepTemplate,
  type OpResult,
  type AppState,
  type PlanReview,
  type PrepPlan,
  type SourceItem,
} from '@autoday/core';
import { getStore, useStore } from '../../store/appStore.ts';
import { blockMessage, runPlanCommit, usePlanCommitReadiness } from '../../prep/commit.ts';
import { planReviews, usePrepDrafts, useQuarter } from '../../prep/drafts.ts';
import { useUi } from '../../App.tsx';
import { Button, Field, inputCls } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { randomId } from '../../platform.ts';
import { useLang } from '../../i18n/i18n.ts';
import { calendarName, formatMinutes, localizeError } from '../../i18n/format.ts';
import { PendingNotice, PlanPreview, type CommitResult, type ResolvedCommit } from './PlanPreview.tsx';
import { PlanTimeline, PrepStatusChip } from './PlanTimeline.tsx';
import { RuleOffer } from './RuleOffer.tsx';
import { TemplateEditor } from './TemplateEditor.tsx';
import {
  deadlineWhen,
  defaultTemplateId,
  endedText,
  futureSessionCount,
  isReplannable,
  planProgress,
  planTimelineRows,
  shouldOfferRule,
  signalText,
  sortedTemplates,
  starterCalendarId,
  stepLine,
  windowPhrase,
  type TimelineRow,
} from './prepText.ts';

/** Vanlig planändring (mall, regel, avfärda, behåll krock): en lokal operation som synkas som vanligt. */
async function runPrepOp(fn: (s: AppState) => OpResult, ok: string): Promise<boolean> {
  const store = getStore();
  try {
    await store.run(fn);
    toast({ kind: 'ok', text: ok });
    return true;
  } catch (e) {
    toast({ kind: 'error', text: errorText(e) });
    return false;
  }
}

const sourceIssue = (r: PlanReview) => r.signals.some((s) => s.kind === 'source-gone' || s.kind === 'source-missing' || s.kind === 'source-review');

/**
 * Förberedelseplanen i källpostens "Förbered" (ItemDetail → SourceDetail):
 *  - aktiv plan: statuskort med tidslinje, tillstånd i text + ikon, granskningssignaler och "Avsluta planen"
 *  - omplanering: förhandsvisning av exakt vad som ändras, godkänns med ett klick
 *  - ingen plan (open): startmall eller mallväljare + förhandsvisning av en ny plan
 *  - avfärdat förslag: "Förslaget är dolt för det här datumet. Visa igen"
 * Förslag skapar ingenting; bara ett godkännande ändrar kalendern (prep/commit.ts).
 */
export function PlanPanel({
  source,
  srcLabel,
  open,
  onOpenChange,
  reviewFirst,
  onManual,
  onFocusSection,
}: {
  source: SourceItem;
  srcLabel: string;
  /** Förhandsvisningen av en ny plan är öppen (SourceDetail: intent 'plan' eller "Planera förberedelser"). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** intent 'plan': en aktiv plan som behöver ses över öppnas direkt i omplaneringen. */
  reviewFirst: boolean;
  /** "Välj tider själv": det manuella flödet i SourceDetail. */
  onManual: () => void;
  /** Fokus till sektionens rubrik när det fokuserade elementet försvinner (plan avslutad, förslag stängt). */
  onFocusSection: () => void;
}) {
  const { state } = useStore();
  const { t } = useLang();
  const store = getStore();
  useQuarter(); // ritar om när kvarten byts: pass som passerat visas som passerade
  const plan = planForSource(state, source.key);
  const active = plan?.status === 'active' ? plan : undefined;
  const review = active ? (planReviews(state, store.clock.now().epochMilliseconds).get(source.key) ?? null) : null;
  const [replan, setReplan] = useState<{ include: string[]; restore: string[] } | null>(() =>
    reviewFirst && review?.needsReview && review.signals.some(isReplannable) && !sourceIssue(review) ? { include: [], restore: [] } : null,
  );
  const [offer, setOffer] = useState<{ templateId: string; templateName: string } | null>(null);
  const [focusCard, setFocusCard] = useState(false);

  if (active && review) {
    return (
      <div className="grid min-w-0 gap-3">
        {replan ? (
          <ReplanPreview
            plan={active}
            review={review}
            source={source}
            choice={replan}
            onChoice={setReplan}
            onClose={(approved) => {
              setReplan(null);
              setFocusCard(true);
              if (!approved) onFocusSection();
            }}
          />
        ) : (
          <PlanCard plan={active} review={review} source={source} focusOnMount={focusCard} onReplan={(restore) => setReplan({ include: [], restore })} onEnded={onFocusSection} />
        )}
        {offer && <RuleOffer sourceId={source.sourceId} sourceLabel={srcLabel} templateId={offer.templateId} templateName={offer.templateName} onDone={() => setOffer(null)} />}
      </div>
    );
  }

  if (open) {
    return (
      <NewPlan
        source={source}
        onClose={() => {
          onOpenChange(false);
          onFocusSection();
        }}
        onManual={onManual}
        onApproved={(templateId) => {
          const s = getStore().state;
          const tpl = s.prepTemplates?.[templateId];
          if (tpl && shouldOfferRule(s, source.sourceId)) setOffer({ templateId, templateName: tpl.name });
          setFocusCard(true);
          onOpenChange(false);
        }}
      />
    );
  }

  if (isPrepDismissed(state, source.key)) {
    return (
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
        <EyeOff size={16} aria-hidden className="shrink-0" />
        <span>{t('Förslaget är dolt för det här datumet.', 'The suggestion is hidden for this date.')}</span>
        <Button
          variant="ghost"
          className="min-h-10 px-3 font-medium text-accent"
          onClick={async () => {
            if (await runPrepOp((s) => restorePrepSuggestion(s, randomId('prep_'), source.key, store.clock.now()), t('Förslaget visas igen.', 'The suggestion is shown again.'))) onOpenChange(true);
          }}
        >
          {t('Visa igen', 'Show again')}
        </Button>
      </p>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// Ny plan: startmall, mallväljare och förhandsvisning
// ---------------------------------------------------------------------------

function NewPlan({ source, onClose, onManual, onApproved }: { source: SourceItem; onClose: () => void; onManual: () => void; onApproved: (templateId: string) => void }) {
  const { state } = useStore();
  const ui = useUi();
  const { t } = useLang();
  const ids = useId();
  const quarter = useQuarter();
  const drafts = usePrepDrafts().result;
  const [nonce, setNonce] = useState(0);
  const [chosen, setChosen] = useState<string | null>(() => defaultTemplateId(state, source.sourceId));
  const [editor, setEditor] = useState<{ id: string | null } | null>(null);
  const templates = sortedTemplates(state);
  const templateId = chosen && state.prepTemplates?.[chosen] ? chosen : defaultTemplateId(state, source.sourceId);
  const proposal = useMemo(
    () => (templateId ? proposePlan(state, { sourceKey: source.key, templateId, now: getStore().clock.now() }) : null),
    // quarter/nonce: räkna om när kvarten byts eller ett godkännande avvisats (nytt förslag med aktuell tid).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, source.key, templateId, quarter, nonce],
  );
  const rule = state.prepRules?.[prepRuleIdFor(source.sourceId)];
  const fromRule = rule?.status === 'active' && rule.templateId === templateId;
  const hasDraft = !!drafts?.drafts.some((d) => d.sourceKey === source.key);

  const editorSheet = editor && (
    <TemplateEditor
      templateId={editor.id}
      onClose={() => setEditor(null)}
      onSaved={(id) => {
        setChosen(id);
        setNonce((n) => n + 1);
      }}
    />
  );

  if (!templates.length || !proposal) {
    return (
      <>
        <StarterOffer onCreated={(id) => setChosen(id)} onCustom={() => setEditor({ id: null })} onCancel={onClose} />
        {editorSheet}
      </>
    );
  }

  const picker = (
    <div className="grid gap-2">
      {templates.length > 1 ? (
        <Field label={t('Mall', 'Template')} htmlFor={`${ids}-tpl`}>
          <select id={`${ids}-tpl`} className={inputCls} value={templateId ?? ''} onChange={(e) => setChosen(e.target.value)}>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <p className="text-sm [overflow-wrap:anywhere]">
          <span className="text-muted">{t('Mall:', 'Template:')}</span> {templates[0]!.name}
        </p>
      )}
      <div className="flex flex-wrap gap-x-1">
        <Button variant="ghost" className="min-h-10 px-2 text-accent" icon={<Pencil size={14} aria-hidden />} onClick={() => setEditor({ id: templateId })}>
          {t('Ändra mallen', 'Change the template')}
        </Button>
        <Button variant="ghost" className="min-h-10 px-2 text-accent" icon={<Plus size={14} aria-hidden />} onClick={() => setEditor({ id: null })}>
          {t('Ny mall', 'New template')}
        </Button>
        <Button variant="ghost" className="min-h-10 px-2 text-accent" icon={<ListChecks size={14} aria-hidden />} aria-haspopup="dialog" onClick={() => ui.openPrepPlans()}>
          {t('Hantera mallar', 'Manage templates')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-accent-line p-3">
      <PlanPreview
        proposal={proposal}
        userKey={templateId ?? ''}
        extra={fromRule ? { origin: 'rule', ruleId: rule!.id } : { origin: 'manual' }}
        picker={picker}
        secondary={
          hasDraft ? (
            <Button
              variant="ghost"
              onClick={async () => {
                if (await runPrepOp((s) => dismissPrepSuggestion(s, randomId('prep_'), source.key, getStore().clock.now()), t('Förslaget är dolt för det här datumet.', 'The suggestion is hidden for this date.'))) onClose();
              }}
            >
              {t('Inte nu', 'Not now')}
            </Button>
          ) : null
        }
        onRefresh={() => setNonce((n) => n + 1)}
        onApproved={() => onApproved(proposal.templateId ?? templateId ?? '')}
        onCancel={onClose}
        onEditTemplate={() => setEditor({ id: templateId })}
        onManual={onManual}
      />
      {editorSheet}
    </div>
  );
}

/**
 * Första gången: ett lugnt erbjudande om den valfria startmallen. Ingenting sparas förrän användaren
 * väljer den; mallen blir då användarens egen (texten följer språket vid skapandet och översätts aldrig).
 */
function StarterOffer({ onCreated, onCustom, onCancel }: { onCreated: (id: string) => void; onCustom: () => void; onCancel: () => void }) {
  const { state } = useStore();
  const { t, lang } = useLang();
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [opId] = useState(() => randomId('tpl_'));
  const content = starterPrepTemplate(lang, starterCalendarId(state));
  const total = content.steps.reduce((n, s) => n + s.durationMinutes, 0);

  async function use() {
    if (busy) return;
    setBusy(true);
    const store = getStore();
    let id = '';
    const ok = await runPrepOp((s) => {
      const r = createPrepTemplate(s, opId, starterPrepTemplate(lang, starterCalendarId(s)), store.clock.now());
      id = r.id;
      return r;
    }, t('Mallen är sparad. Här är förslaget.', 'The template is saved. Here is the suggestion.'));
    setBusy(false);
    if (ok && id) onCreated(id);
  }

  return (
    <div className="rounded-lg border border-accent-line bg-accent-soft/40 p-3 text-sm" role="group" aria-labelledby={`${ids}-h`}>
      <p id={`${ids}-h`} className="font-semibold">
        {t('Förbered i flera steg', 'Prepare in several steps')}
      </p>
      <p className="mt-1 text-muted">
        {t(
          'Beskriv en gång hur du brukar förbereda dig. AutoDay föreslår sedan en genomförbar plan inför datumet – du ser alla tider innan något läggs in och godkänner hela planen med ett klick.',
          'Describe once how you usually prepare. AutoDay then suggests a workable plan before the date – you see every time before anything is added and approve the whole plan with one click.',
        )}
      </p>
      <div className="mt-3 rounded-lg border border-border bg-panel p-3">
        <p className="font-medium [overflow-wrap:anywhere]">{content.name}</p>
        <ol className="mt-1 list-decimal space-y-0.5 pl-5">
          {content.steps.map((s, i) => (
            <li key={i} className="[overflow-wrap:anywhere]">
              {stepLine(s, content.dayCount)}
            </li>
          ))}
        </ol>
        <p className="mt-1 text-xs text-muted">
          {t(`Totalt ${formatMinutes(total)}, ${windowPhrase(content)}, i kalendern ${calendarName(state.calendars[content.calendarId], content.calendarId)}.`, `Total ${formatMinutes(total)}, ${windowPhrase(content)}, in the calendar ${calendarName(state.calendars[content.calendarId], content.calendarId)}.`)}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="primary" disabled={busy} onClick={() => void use()}>
          {t('Använd mallen', 'Use the template')}
          <span className="sr-only"> {content.name}</span>
        </Button>
        <Button onClick={onCustom}>{t('Skapa egen mall', 'Create your own template')}</Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('Avbryt', 'Cancel')}
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted">{t('Mallen sparas först när du väljer den, och du kan ändra den när som helst.', 'The template is only saved when you choose it, and you can change it at any time.')}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aktiv plan: statuskort, signaler, omplanering och avslut
// ---------------------------------------------------------------------------

function PlanCard({ plan, review, source, focusOnMount, onReplan, onEnded }: { plan: PrepPlan; review: PlanReview; source: SourceItem; focusOnMount: boolean; onReplan: (restore: string[]) => void; onEnded: () => void }) {
  const { state } = useStore();
  const ui = useUi();
  const { t } = useLang();
  const ids = useId();
  const readiness = usePlanCommitReadiness();
  const headRef = useRef<HTMLHeadingElement>(null);
  const [ending, setEnding] = useState(false);
  useEffect(() => {
    if (focusOnMount) headRef.current?.focus();
  }, [focusOnMount]);
  const tz = state.settings.timeZone;
  const rows = planTimelineRows(plan, review, tz);
  const progress = planProgress(review);
  const basis = currentBasis(state, source);
  const canReplan = review.needsReview && review.signals.some(isReplannable) && !sourceIssue(review) && !review.deadlinePassed;
  const total = plan.steps.reduce((n, s) => n + s.durationMinutes, 0);

  const rowActions = (r: TimelineRow) => {
    const st = review.steps.find((s) => s.stepId === r.stepId);
    if (!st) return null;
    if (st.state === 'removed') {
      return review.deadlinePassed ? null : (
        <Button className="min-h-10 px-3" onClick={() => onReplan([st.stepId])}>
          {t('Återställ steget', 'Restore the step')}
          <span className="sr-only">: {r.title}</span>
        </Button>
      );
    }
    if (!st.event) return null;
    return (
      <Button variant="ghost" className="min-h-10 px-3 text-accent" aria-label={t(`Öppna passet ${r.title}`, `Open the session ${r.title}`)} onClick={() => ui.openItem({ ref: st.eventId })}>
        {t('Öppna', 'Open')}
      </Button>
    );
  };

  return (
    <section className="grid min-w-0 gap-3 rounded-lg border border-accent-line bg-panel p-3" aria-labelledby={`${ids}-h`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 id={`${ids}-h`} ref={headRef} tabIndex={-1} className="flex items-center gap-2 font-semibold">
          <CalendarPlus size={16} aria-hidden className="shrink-0 text-accent" />
          {t('Förberedelseplan', 'Preparation plan')}
        </h4>
        <PrepStatusChip text={progress.text} tone={progress.tone} icon={progress.icon} />
      </div>
      <p className="-mt-2 text-xs text-muted [overflow-wrap:anywhere]">
        {t('Mall', 'Template')}: {plan.snapshot.name} · {t(`totalt ${formatMinutes(total)}`, `total ${formatMinutes(total)}`)}
      </p>
      {progress.note && <p className="text-sm">{progress.note}</p>}
      <PlanTimeline
        rows={rows}
        deadline={{ when: deadlineWhen(basis, tz), sentence: `${t('Deadline', 'Deadline')} ${deadlineWhen(basis, tz, true)}.` }}
        label={t('Planens steg i tidsordning', 'The plan’s steps in time order')}
        actions={rowActions}
      />
      {review.deadlinePassed && <p className="text-sm text-muted">{t('Deadlinen har passerat. Planen föreslår inga ändringar längre.', 'The deadline has passed. The plan suggests no more changes.')}</p>}
      {review.needsReview && <SignalList plan={plan} review={review} />}
      {readiness.pending?.planId === plan.id && readiness.pending.kind === 'end' && !ending && <PendingNotice text={null} onResult={() => undefined} />}
      {ending ? (
        <EndPlan
          plan={plan}
          onCancel={() => {
            setEnding(false);
            requestAnimationFrame(() => headRef.current?.focus());
          }}
          onEnded={onEnded}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          {canReplan && (
            <Button variant="primary" onClick={() => onReplan([])}>
              {t('Föreslå ändring', 'Suggest a change')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => setEnding(true)}>
            {t('Avsluta planen', 'End the plan')}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Granskningssignaler i klartext. Vid krock kan passet behållas ändå (flaggas inte igen förrän krocken ändras). */
function SignalList({ plan, review }: { plan: PrepPlan; review: PlanReview }) {
  const { t } = useLang();
  const titleOf = (stepId: string) => review.steps.find((s) => s.stepId === stepId)?.title ?? '';
  return (
    <div className="rounded-lg border border-warn/60 bg-warn-bg p-3 text-sm">
      <p className="flex items-center gap-2 font-semibold">
        <TriangleAlert size={16} aria-hidden className="shrink-0 text-warn" /> {t('Planen behöver ses över', 'The plan needs a look')}
      </p>
      <ul className="mt-1 grid gap-1">
        {review.signals.map((s, i) => (
          <li key={s.kind === 'conflict' ? s.fingerprint : `${s.kind}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{signalText(s, titleOf)}</span>
            {s.kind === 'conflict' && (
              <Button
                className="min-h-10 px-3"
                onClick={() => void runPrepOp((st) => acknowledgePlanConflicts(st, randomId('ack_'), plan.id, [s.fingerprint], getStore().clock.now()), t('Passet ligger kvar trots krocken.', 'The session stays despite the clash.'))}
              >
                {t('Behåll ändå', 'Keep anyway')}
                <span className="sr-only">: {titleOf(s.stepId)}</span>
              </Button>
            )}
          </li>
        ))}
      </ul>
      {sourceIssue(review) && <p className="mt-1 text-xs">{t('Granska källposten först – se rutan överst i den här vyn. Planen flyttar ingenting av sig själv.', 'Review the source item first – see the box at the top of this view. The plan never moves anything by itself.')}</p>}
    </div>
  );
}

function ReplanPreview({ plan, review, source, choice, onChoice, onClose }: { plan: PrepPlan; review: PlanReview; source: SourceItem; choice: { include: string[]; restore: string[] }; onChoice: (c: { include: string[]; restore: string[] }) => void; onClose: (approved: boolean) => void }) {
  const { state } = useStore();
  const ui = useUi();
  const quarter = useQuarter();
  const [nonce, setNonce] = useState(0);
  const proposal = useMemo(
    () => proposePlan(state, { sourceKey: source.key, now: getStore().clock.now(), include: choice.include, restore: choice.restore }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, source.key, choice, quarter, nonce],
  );
  const toggle = (key: 'include' | 'restore') => (stepId: string) => onChoice({ ...choice, [key]: choice[key].includes(stepId) ? choice[key].filter((x) => x !== stepId) : [...choice[key], stepId] });
  // Planen kan ha avslutats på en annan enhet medan förhandsvisningen var öppen.
  if (proposal.planId !== plan.id) return null;
  return (
    <div className="rounded-lg border border-accent-line p-3">
      <PlanPreview
        proposal={proposal}
        userKey={JSON.stringify(choice)}
        extra={{ include: choice.include, restore: choice.restore }}
        reasons={review.needsReview ? <SignalList plan={plan} review={review} /> : null}
        onRefresh={() => setNonce((n) => n + 1)}
        onApproved={() => onClose(true)}
        onCancel={() => onClose(false)}
        onToggleInclude={toggle('include')}
        onToggleRestore={toggle('restore')}
        onOpenEvent={(id) => ui.openItem({ ref: id })}
      />
    </div>
  );
}

/**
 * Avsluta planen: "Behåll passen" eller "Ta bort kommande pass" (pass som du ändrat själv och klara pass
 * ligger alltid kvar). Samma atomiska väg som godkännanden; aldrig ett "klart" utan belägg.
 */
function EndPlan({ plan, onCancel, onEnded }: { plan: PrepPlan; onCancel: () => void; onEnded: () => void }) {
  const { state } = useStore();
  const { t } = useLang();
  const ids = useId();
  const readiness = usePlanCommitReadiness();
  const [opId, setOpId] = useState(() => randomId('end_'));
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [unknown, setUnknown] = useState<string | null>(null);
  const [removing, setRemoving] = useState(0);
  const firstRef = useRef<HTMLButtonElement>(null);
  useEffect(() => firstRef.current?.focus(), []);
  const future = futureSessionCount(state, plan, getStore().clock.now());
  const blocked = readiness.ready ? null : (readiness.message ?? (readiness.block ? blockMessage(readiness.block) : null));

  function handle(out: CommitResult, removed: number) {
    if (out.ok) {
      toast({ kind: 'ok', text: out.duplicate ? t('Planen var redan avslutad.', 'The plan had already ended.') : endedText(removed) });
      onEnded();
      return;
    }
    if (out.kind === 'unknown') {
      setUnknown(out.message);
      return;
    }
    // Inget ändrades. Ett nytt försök skickas med nytt operations-ID.
    if (out.kind !== 'blocked') setOpId(randomId('end_'));
    setNotice(out.kind === 'not-applied' ? t('Planen avslutades inte. Inget ändrades.', 'The plan was not ended. Nothing changed.') : out.kind === 'error' ? localizeError(out.message) : out.message);
  }

  async function end(removeFuture: boolean) {
    if (inFlight.current || !readiness.ready) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    const removed = removeFuture ? future : 0;
    setRemoving(removed);
    let out: CommitResult;
    try {
      out = await runPlanCommit({ kind: 'end', opId, planId: plan.id, sourceKey: plan.sourceKey, expectedPlanRevision: plan.revision, removeFuture });
    } catch (e) {
      out = { ok: false, kind: 'error', message: errorText(e) };
    }
    inFlight.current = false;
    setBusy(false);
    handle(out, removed);
  }

  return (
    <div className="rounded-lg border border-border-strong bg-raised p-3 text-sm" role="group" aria-labelledby={`${ids}-h`}>
      <p id={`${ids}-h`} className="font-semibold">
        {t('Avsluta planen?', 'End the plan?')}
      </p>
      <p className="mt-1 text-muted">
        {t('Planen slutar följa deadlinen och föreslår inga fler ändringar. Klara pass och pass som du ändrat själv ligger alltid kvar.', 'The plan stops following the deadline and suggests no more changes. Done sessions and sessions you changed yourself always stay.')}
      </p>
      <div aria-live="polite" className="empty:hidden">
        {notice && notice !== blocked && <p className="mt-2 rounded-lg border border-warn/60 bg-warn-bg px-3 py-2">{notice}</p>}
        {unknown && (
          <div className="mt-2">
            <PendingNotice
              text={unknown}
              onResult={(r: ResolvedCommit) => {
                setUnknown(null);
                if (r) handle(r, removing);
              }}
            />
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button ref={firstRef} disabled={busy || !readiness.ready} aria-describedby={blocked ? `${ids}-why` : undefined} onClick={() => void end(false)}>
          {t('Behåll passen', 'Keep the sessions')}
        </Button>
        {future > 0 && (
          <Button variant="danger" disabled={busy || !readiness.ready} aria-describedby={blocked ? `${ids}-why` : undefined} onClick={() => void end(true)}>
            {future === 1 ? t('Ta bort 1 kommande pass', 'Remove 1 upcoming session') : t(`Ta bort ${future} kommande pass`, `Remove ${future} upcoming sessions`)}
          </Button>
        )}
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          {t('Avbryt', 'Cancel')}
        </Button>
      </div>
      {blocked && (
        <p id={`${ids}-why`} className="mt-1 text-xs text-warn">
          {blocked}
        </p>
      )}
    </div>
  );
}
